import { Bell, CheckCheck } from "lucide-react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/core/components/ui/avatar";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import {
  selectUnreadCount,
  useNotificationStore,
  type InAppNotification,
} from "@/core/stores/notification.store";

const NOTIFICATION_CENTER_PATH = "/app/notifications/center";

/** Temps relatif depuis `receivedAt`, via les clés `layout.notifications.timeAgo.*`. */
function timeAgo(receivedAt: string): { key: TFunctionType; count: number } {
  const minutes = Math.max(
    1,
    Math.floor((Date.now() - new Date(receivedAt).getTime()) / 60000)
  );
  if (minutes < 60) {
    return { key: "layout.notifications.timeAgo.minutes", count: minutes };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { key: "layout.notifications.timeAgo.hours", count: hours };
  }
  return { key: "layout.notifications.timeAgo.days", count: Math.floor(hours / 24) };
}

type NotificationDropdownProps = {
  trigger: ReactElement;
  defaultOpen?: boolean;
  align?: "start" | "center" | "end";
};

export function NotificationDropdown({
  trigger,
  defaultOpen,
  align = "end",
}: NotificationDropdownProps) {
  const { t } = useTypedTranslation();
  const navigate = useNavigate();
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore(selectUnreadCount);
  const markRead = useNotificationStore((s) => s.markRead);
  const markAllRead = useNotificationStore((s) => s.markAllRead);

  const openNotification = (notification: InAppNotification) => {
    markRead(notification.id);
    navigate(NOTIFICATION_CENTER_PATH);
  };

  const renderItem = (notification: InAppNotification) => {
    const ago = timeAgo(notification.receivedAt);
    return (
      <DropdownMenuItem
        key={notification.id}
        className="gap-3 px-2 py-3"
        onSelect={() => openNotification(notification)}
      >
        <Avatar className="size-9">
          <AvatarFallback>
            <Bell className="size-4" />
          </AvatarFallback>
        </Avatar>
        <div className="flex w-full min-w-0 flex-col items-start">
          <NoTranslate as="span" className="line-clamp-1 text-sm font-medium">
            {notification.title}
          </NoTranslate>
          <NoTranslate
            as="span"
            className="line-clamp-2 text-sm text-muted-foreground"
          >
            {notification.body}
          </NoTranslate>
          <span className="text-xs text-muted-foreground">
            {t(ago.key, { count: ago.count })}
          </span>
        </div>
        {!notification.read && (
          <div className="size-1.5 shrink-0 rounded-full bg-primary" />
        )}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[min(100vw-1.5rem,24rem)] max-w-xs sm:max-w-96"
        align={align}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center justify-between gap-3 pb-2.5">
            <span className="text-sm font-normal uppercase text-muted-foreground">
              {t("layout.notifications.title")}
            </span>
            <div className="flex items-center gap-1.5">
              {unreadCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-primary/10 font-normal text-primary"
                >
                  {t("layout.notifications.new", { count: unreadCount })}
                </Badge>
              )}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={t("layout.notifications.markAllRead")}
                  title={t("layout.notifications.markAllRead")}
                  onClick={markAllRead}
                >
                  <CheckCheck className="size-4" />
                </Button>
              )}
            </div>
          </DropdownMenuLabel>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="mt-0 h-0.5" />

        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-muted-foreground">
              <Bell className="size-6" />
              <span className="text-sm">{t("layout.notifications.empty")}</span>
            </div>
          ) : (
            notifications.map((notification, index) => (
              <div key={notification.id}>
                {renderItem(notification)}
                {index !== notifications.length - 1 && (
                  <DropdownMenuSeparator />
                )}
              </div>
            ))
          )}
        </div>

        <DropdownMenuSeparator className="mb-0 h-0.5" />

        <DropdownMenuItem
          className="justify-center py-2.5 text-sm font-medium text-primary"
          onSelect={() => navigate(NOTIFICATION_CENTER_PATH)}
        >
          {t("layout.notifications.seeAll")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
